import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import Button from 'components/inputs/button';
import Tooltip from 'components/layout/tooltip';
import { ProjectSettingCodeType } from 'data/types';
import { updateProjectSetting } from 'features/project';
import { getProjectSetting, getProjectSettingValue } from 'utils/project';

type ProjectSettingButtonSelectorProps = {
  code: ProjectSettingCodeType;
  options: {
    label: string;
    value: string;
    description?: string;
    disabled?: boolean;
    tooltip?: string;
    fullWidth?: boolean;
  }[];
  onChange?: (value?: string) => void;
  // Interception point (AHA-6681): return false to swallow the click —
  // nothing is dispatched and onChange never fires. The caller owns any
  // deferred dispatch (e.g. after a confirm dialog).
  beforeChange?: (nextValue?: string) => boolean;
  wrap?: boolean;
  columns?: number;
};

export default function ProjectSettingButtonSelector({
  code,
  options,
  onChange,
  beforeChange,
  wrap,
  columns
}: ProjectSettingButtonSelectorProps) {
  const dispatch = useAppDispatch();
  const developerMode = useAppSelector((state) => state.ui.developerMode);
  const setting = getProjectSetting(code);

  if (!setting) {
    return <div className='text-red-500'>{code} not found!</div>;
  }

  const handleOptionClick = (value: string) => {
    const newValue = getProjectSettingValue(code) === value ? undefined : value;

    if (beforeChange && !beforeChange(newValue)) {
      return;
    }

    dispatch(
      updateProjectSetting({
        attributeCode: code,
        value: newValue
      })
    );

    onChange?.(newValue);
  };

  const isGrid = typeof columns === 'number';

  return (
    <div
      className={clsx(
        'w-full gap-5',
        isGrid ? 'grid' : 'flex items-center',
        !isGrid && wrap && 'flex-wrap'
      )}
      style={
        isGrid
          ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
          : undefined
      }
      title={developerMode ? code : undefined}
      data-attribute-code={code}
    >
      {options.map((option) => (
        <div
          key={option.value}
          className={clsx(
            'relative flex flex-col items-center gap-2',
            !isGrid && 'min-w-[150px]',
            !wrap && !isGrid && 'grow',
            isGrid && option.fullWidth && 'col-span-full'
          )}
        >
          <Button
            text={option.label}
            color={
              getProjectSettingValue(code) === option.value
                ? 'secondary'
                : 'white'
            }
            size='lg'
            fullWidth
            disabled={option.disabled}
            onClick={() => handleOptionClick(option.value)}
          />
          {option.disabled && option.tooltip && (
            <Tooltip text={option.tooltip} />
          )}
          {option.description && (
            <div className='text-center text-sm text-gray-500'>
              {option.description}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
